import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCustomerData, fetchSampleTypes, proceedsamplerequestorder, CheckoutSampleRequest } from '../../Service/ApiService';
import { useLocation } from 'react-router-dom';
import { showAlert } from 'utils/ErrorMessage';
import { showSuccessAlert } from 'utils/SuccessMessage';
import * as Yup from "yup";

const barcodeItemSchema = Yup.object().shape({
  sampleType: Yup.object()
    .nullable()
    .required("Please select sample type"),

  sampleQuantity: Yup.mixed()
    .required("Quantity is required")
    .test(
      "is-valid-number",
      "Quantity must be at least 1",
      (value) => {
        if (value === null || value === undefined || value === "") return false;
        return Number(value) >= 1;
      }
    )
});

const SampleSelectionController = () => {
  const navigate = useNavigate();
  const allowNavigationRef = useRef(false);
  const location = useLocation();
  const incomingData = location.state?.barcodeData || [];
  const isReplication = Boolean(location.state?.isReplication);
  const isEditMode = Boolean(location.state?.isEditMode);
  const sampleRequestReplicateRefNo = location.state?.sampleRequestReplicateRefNo || '';
  const [sampleRequestOrderId, setSampleRequestOrderId] = useState(location.state?.sampleRequestOrderId || '');
  const [sampleRequestDocNo, setSampleRequestDocNo] = useState(location.state?.sampleRequestDocNo || '');
  const [isDirty, setIsDirty] = useState(
    location.state?.hasUnsavedChanges !== undefined
      ? Boolean(location.state.hasUnsavedChanges)
      : !isEditMode
  );

  const [sampleTypes, setSampleTypes] = useState([]);
  const [selectedSampleType, setSelectedSampleType] = useState(null);
  const [sampleCategory, setSampleCategory] = useState(location.state?.sampleCategory || null);

  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(
    location.state?.selectedCustomer ||
    (location.state?.partyCode
      ? {
        value: location.state.partyCode,
        label: location.state.partyCode,
        apId: location.state?.apId || ''
      }
      : null)
  );
  const [searchText, setSearchText] = useState('');

  const [barcodeData, setBarcodeData] = useState(() =>
    (incomingData || []).map(item => ({ ...item, sampleType: item.sampleType, sampleQuantity: item.sampleQuantity ?? null }))
  );

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const confvalue = JSON.parse(localStorage.getItem("confvalue")) || {};

  // Remark
  //const [sampleRequestOrderId, setampleRequestOrderId] = useState(null);
  const [openRemarkDialog, setOpenRemarkDialog] = useState(false);
  const [remark, setRemark] = useState(location.state?.remark || '');
  const [remarkError, setRemarkError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const barcodeArraySchema = Yup.array().of(barcodeItemSchema);

  useEffect(() => {
    const message = "If you leave this screen, all entered data will be lost. Do you want to continue?";
    const handleBeforeUnload = (event) => {
      if (allowNavigationRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    const handlePopState = () => {
      if (allowNavigationRef.current) return;
      if (window.confirm(message)) {
        allowNavigationRef.current = true;
        navigate('/view-sample-request', { replace: true });
      } else {
        window.history.pushState({ sampleRequestGuard: true }, '', window.location.href);
      }
    };

    window.history.pushState({ sampleRequestGuard: true }, '', window.location.href);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  useEffect(() => {
    getSampleTypes();
  }, []);

  useEffect(() => {
    getCustomerDetails(user.userCode, searchText);
  }, [searchText]);

  useEffect(() => {
    if (!location.state?.isReplication || !location.state?.partyCode) {
      return;
    }

    const replicatedCustomer = {
      value: location.state.partyCode,
      label: [location.state?.partyName, location.state?.city, location.state.partyCode].filter(Boolean).join(' - '),
      apId: location.state?.apId || location.state?.customerId || ''
    };

    setCustomerOptions(prev => {
      if (prev.some((option) => option.value === replicatedCustomer.value)) {
        return prev;
      }

      return [...prev, replicatedCustomer];
    });

    setSelectedCustomer((prev) => {
      if (prev && prev.value === replicatedCustomer.value) {
        return prev;
      }

      return replicatedCustomer;
    });
  }, [location.state?.isReplication, location.state?.partyCode, location.state?.partyName, location.state?.city, location.state?.apId, location.state?.customerId]);


  useEffect(() => {
    console.log('customerOptions updated:', customerOptions);
  }, [customerOptions]);

  const handleInputChange = (inputValue) => {
    setSearchText(inputValue);
  };

  const getCustomerDetails = async (userCode, searchText) => {
    if (searchText && searchText.length < 3) {
      return;
    }

    try {
      const res = await getCustomerData(userCode, searchText);
      if (res && res.success == "true") {
        const options = (res.customerList || []).reduce((uniqueList, customer) => {
          const existingCustomer = uniqueList.find((item) => item.value === customer.partyCode);

          if (!existingCustomer) {
            uniqueList.push({
              value: customer.partyCode,
              label: `${customer.partyName} - ${customer.city} - ${customer.partyCode}`,
              apId: customer.apId
            });
          }

          return uniqueList;
        }, []);
        setCustomerOptions(options);
      } else {
        setCustomerOptions([]);
      }
    } catch (err) {
      console.error("Customer Details load error:", err);
      return null;
    }
  };

  const getSampleTypes = async () => {
    try {
      const res = await fetchSampleTypes();
      if (res && res.success == "true") {
        const sampleTypeOptions = res.activeConferenceList.map(item => ({
          value: item.sampleTypeId,
          label: item.sampleTypeName
        }));

        setSampleTypes(sampleTypeOptions);
      } else {
        setSampleTypes([]);
      }
    } catch (err) {
      console.error("Sample Types load error:", err);
      return [];
    }
  };

  const setBarcodeSampleType = (index, sampleTypeOption) => {
    setIsDirty(true);
    setBarcodeData(prev =>
      prev.map((item, i) =>
        i === index
          ? {
            ...item,
            sampleType: sampleTypeOption,
            errors: { ...item.errors, sampleType: "" } // clear error
          }
          : item
      )
    );
  };


  const setBarcodeSampleQuantity = (index, sampleQuantity) => {
    setIsDirty(true);
    setBarcodeData(prev =>
      prev.map((item, i) =>
        i === index
          ? {
            ...item,
            sampleQuantity: sampleQuantity,
            errors: { ...item.errors, sampleQuantity: "" } // clear error
          }
          : item
      )
    );
  };


  // const submitSampleRequest = async () => {
  //   try {
  //     if (!sampleCategory) {
  //       showAlert("Please select sample category");
  //       return;
  //     }

  //     if (!selectedCustomer) {
  //       showAlert("Please select party");
  //       return;
  //     }

  //     console.log('Submitting sample request with barcodeData:', barcodeData);

  //     const userCode = user?.userCode;
  //     const conferenceId = confvalue?.cID;
  //     const apId = selectedCustomer?.apId || '';
  //     const partyCode = selectedCustomer?.value || '';

  //     // incoming barcode data passed from prev screen
  //     const incomingData = location.state?.barcodeData || [];

  //     // build sample details
  //     const SampleOrderDetails = barcodeData
  //       .filter(item => item.sampleType && item.sampleQuantity > 0)
  //       .map((item, i) => ({
  //         sampleType: item.sampleType.label,
  //         quantity: item.sampleQuantity,

  //         // merge material fields from incomingData
  //         materialID: incomingData[i]?.materialID || "",
  //         materialCode: incomingData[i]?.materialCode || "",
  //         materialDesc: incomingData[i]?.materialDesc || "",
  //         mainGroup: incomingData[i]?.mainGroup || "",
  //         materialVertical: incomingData[i]?.materialVertical || "",
  //         barcode: incomingData[i]?.barcode || ""
  //       }));

  //     // validation
  //     const valid = await validateBarcodeItems();
  //     if (!valid) {
  //       showAlert("Please fix highlighted errors");
  //       return;
  //     }

  //     const payload = {
  //       userCode,
  //       apId,
  //       partyCode,
  //       conferenceId,
  //       sampleCategory,
  //       sampleOrderDetailsListArray: {
  //         SampleOrderDetails
  //       }
  //     };

  //     const response = await proceedsamplerequestorder(payload);
  //     if (response.success === 'true') {
  //       setampleRequestOrderId(response.samReqOrderId ?? null);
  //       // showSuccessAlert("Bill Details Uploaded Successfully!", "Successfully Uploaded");
  //       setOpenRemarkDialog(true);
  //       console.log("API RESPONSE →", response);
  //     }

  //     return;
  //   } catch (err) {
  //     showAlert("Sample request not submitted!");
  //   }
  // };

  // // Remark
  // const submitRemark = async () => {
  //   try {
  //     const response = await submitSampleRequestRemarkAPI(sampleRequestOrderId, remark);
  //     if (response.success) {
  //       showSuccessAlert("Remark Submitted Successfully!", "Remark Submitted");
  //       setOpenRemarkDialog(false);
  //       setTimeout(() => {
  //         navigate("/sample-request-dashboard");
  //       }, 500);
  //     }

  //     return;
  //   } catch (err) {
  //     showAlert("Remark not submitted!");
  //   }
  // }

  const submitSampleRequest = async () => {
    try {
      if (!sampleCategory) {
        showAlert("Please select sample category");
        return;
      }

      if (!selectedCustomer) {
        showAlert("Please select party");
        return;
      }

      const valid = await validateBarcodeItems();
      if (!valid) {
        showAlert("Please fix highlighted errors");
        return;
      }

      await submitRemark(false);
    } catch (err) {
      showAlert("Something went wrong while validating!");
    }
  };

  const handleAddMore = async () => {
    if (isDirty || !sampleRequestOrderId) {
      showAlert("Please save the sample request before adding more materials");
      return;
    }

    if (!sampleCategory) {
      showAlert("Please select sample category");
      return;
    }

    if (!selectedCustomer) {
      showAlert("Please select party");
      return;
    }

    const valid = await validateBarcodeItems();
    if (!valid) {
      showAlert("Please fix highlighted errors");
      return;
    }

    navigate('/sample-request-dashboard', {
      replace: true,
      state: {
        addMoreDraft: {
          barcodeData,
          selectedCustomer,
          sampleCategory,
          isReplication,
          sampleRequestReplicateRefNo,
          sampleRequestOrderId,
          sampleRequestDocNo,
          remark,
          isEditMode: true
        }
      }
    });
  };

  const submitRemark = async (silent = false) => {
    if (isSubmitting) return;

    try {
      setRemarkError("");
      setIsSubmitting(true);

      const userCode = user?.userCode;
      const conferenceId = confvalue?.cID;
      const apId = selectedCustomer?.apId || selectedCustomer?.customerId || location.state?.apId || '';
      const partyCode = selectedCustomer?.value || selectedCustomer?.partyCode || location.state?.partyCode || '';

      console.log('Submitting sample request with barcodeData:', barcodeData);

      const SampleOrderDetails = barcodeData
        .filter(item => item.sampleType && item.sampleQuantity > 0)
        .map((item) => ({
          sampleType: item.sampleType.label,
          quantity: item.sampleQuantity,

          materialID: item.materialID || "",
          materialCode: item.materialCode || "",
          materialDesc: item.materialDesc || "",
          mainGroup: item.mainGroup || "",
          materialVertical: item.materialVertical || "",
          barcode: item.barcode || ""
        }));

      const payload = {
        userCode,
        apId,
        partyCode,
        conferenceId,
        sampleCategory,
        remark,
        isReplicate: isReplication ? 1 : 0,
        sampleRequestReplicateRefNo,
        sampleRequestOrderId,
        sampleOrderDetailsListArray: {
          SampleOrderDetails
        }
      };

      const response = await proceedsamplerequestorder(payload);

      if (response.success === 'true' || response.success === true) {
        setSampleRequestOrderId(response.samReqOrderId || sampleRequestOrderId);
        setSampleRequestDocNo(response.samReqDocNo || sampleRequestDocNo);
        setIsDirty(false);
        if (!silent) showSuccessAlert("Sample request saved successfully!", "Saved");
        // 🔽 DOWNLOAD FILE HERE
        // assuming API returns fileUrl or filePath
        // PDF generation/download will run from Checkout after its API is connected.

        setOpenRemarkDialog(false);
        return response;
      } else {
        showAlert(response.message || "Sample request not saved!");
      }

      return;
    } catch (err) {
      console.error(err);
      showAlert("Sample request not submitted!");
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckout = () => {
    if (!sampleRequestOrderId) {
      showAlert("Please save the sample request before checkout");
      return;
    }

    setRemarkError("");
    setOpenRemarkDialog(true);
  };

  const handleCheckoutSubmit = async () => {
    if (isSubmitting) return;

    try {
      setRemarkError("");
      setIsSubmitting(true);

      const response = await CheckoutSampleRequest(sampleRequestOrderId, remark);

      if (response.success === 'true' || response.success === true) {
        setOpenRemarkDialog(false);
        showSuccessAlert("Sample request checked out successfully!", "Checkout");
        navigate("/view-sample-request");
      } else {
        showAlert(response.message || "Sample request checkout failed!");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      showAlert("Sample request checkout failed!");
    } finally {
      setIsSubmitting(false);
    }
  };


  const validateBarcodeItems = async () => {
    try {
      await barcodeArraySchema.validate(barcodeData, { abortEarly: false });

      // Clear old errors
      setBarcodeData(prev =>
        prev.map(item => ({ ...item, errors: {} }))
      );

      return true;
    } catch (err) {
      if (!err.inner) return false;

      const updated = [...barcodeData];

      err.inner.forEach(e => {
        const match = e.path.match(/\[(\d+)\]/);
        if (!match) return;

        const index = Number(match[1]);
        const field = e.path.split(".")[1]; // "sampleType" or "sampleQuantity"

        if (!updated[index].errors) updated[index].errors = {};
        updated[index].errors[field] = e.message;
      });

      setBarcodeData(updated);
      return false;
    }
  };


  return {
    barcodeData,
    sampleRequestDocNo,
    sampleTypes,
    selectedSampleType,
    setSelectedSampleType,
    customerOptions,
    selectedCustomer,
    setSelectedCustomer: (value) => {
      setSelectedCustomer(value);
      setIsDirty(true);
    },
    sampleCategory,
    setSampleCategory: (value) => {
      setSampleCategory(value);
      setIsDirty(true);
    },
    setBarcodeSampleType,
    setBarcodeSampleQuantity,
    handleAddMore,
    submitSampleRequest,
    openRemarkDialog,
    setOpenRemarkDialog,
    remark,
    setRemark,
    remarkError,
    isSubmitting,
    handleInputChange,
    handleCheckout,
    handleCheckoutSubmit,
    submitRemark
  };
};

export default SampleSelectionController;
